import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
  Percent,
  Shield,
  UserCog,
  Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../services/api';

const REP_TYPE_OPTIONS = [
  {
    value: 'rep1',
    label: 'Internal Representative (Rep1)',
    description: 'Internal business operators',
    icon: Shield,
  },
  {
    value: 'rep2',
    label: 'Senior/Regional Representative (Rep2)',
    description: 'Senior or regional managers',
    icon: UserCog,
  },
  {
    value: 'rep3',
    label: 'Partner Representative (Rep3)',
    description: 'External agency partners',
    icon: Building2,
  },
];

const EXPERIENCE_OPTIONS = [
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Mid-Level' },
  { value: 'senior', label: 'Senior' },
  { value: 'lead', label: 'Lead' },
  { value: 'director', label: 'Director' },
];

const initialFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  country: '',
  repType: '',
  commissionPercentage: 10,
  password: '',
  assignedRegion: '',
  organization: '',
  experienceLevel: 'mid',
  bio: '',
};

export default function AddRepresentativeDialog({
  open,
  onOpenChange,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ ...initialFormData });
  const [errors, setErrors] = useState({});

  const generatePassword = () => {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.repType)
      newErrors.repType = 'Representative type is required';

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (
      formData.phone &&
      !/^\+?[\d\s-]{10,}$/.test(formData.phone.replace(/\s/g, ''))
    ) {
      newErrors.phone = 'Invalid phone format';
    }

    if (
      formData.commissionPercentage < 0 ||
      formData.commissionPercentage > 100
    ) {
      newErrors.commissionPercentage = 'Commission must be between 0 and 100';
    }

    if (formData.repType === 'rep3' && !formData.organization.trim()) {
      newErrors.organization =
        'Organization is required for Partner representatives';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await adminAPI.createRepresentative(formData);
      toast.success('Representative created successfully');

      setFormData({ ...initialFormData });
      setErrors({});
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      const message =
        error.response?.data?.message || 'Failed to create representative';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Representative</DialogTitle>
          <DialogDescription>
            Create a new representative account. Choose the type based on their
            role in the organization.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Representative Type Selection */}
          <div className="space-y-2">
            <Label>
              Representative Type <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-1 gap-2">
              {REP_TYPE_OPTIONS.map(option => {
                const Icon = option.icon;
                const isSelected = formData.repType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleChange('repType', option.value)}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? 'border-[#F2575F] bg-red-50'
                        : 'border-stone-200 hover:border-stone-300 bg-white'
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        isSelected
                          ? 'bg-[#F2575F] text-white'
                          : 'bg-stone-100 text-gray-500'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p
                        className={`text-sm font-medium ${isSelected ? 'text-[#F2575F]' : 'text-gray-900'}`}
                      >
                        {option.label}
                      </p>
                      <p className="text-xs text-gray-500">
                        {option.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            {errors.repType && (
              <p className="text-sm text-red-500">{errors.repType}</p>
            )}
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                First Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={e => handleChange('firstName', e.target.value)}
                placeholder="John"
                className={errors.firstName ? 'border-red-500' : ''}
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">
                Last Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={e => handleChange('lastName', e.target.value)}
                placeholder="Doe"
                className={errors.lastName ? 'border-red-500' : ''}
              />
              {errors.lastName && (
                <p className="text-sm text-red-500">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={e => handleChange('email', e.target.value)}
              placeholder="john.doe@example.com"
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={e => handleChange('phone', e.target.value)}
                placeholder="+1 234 567 8900"
                className={errors.phone ? 'border-red-500' : ''}
              />
              {errors.phone && (
                <p className="text-sm text-red-500">{errors.phone}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={formData.country}
                onChange={e => handleChange('country', e.target.value)}
                placeholder="Bangladesh"
              />
            </div>
          </div>

          {/* Professional Info */}
          <div className="border-t border-stone-200 pt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">
              Professional Information
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="assignedRegion">Assigned Region</Label>
                <Input
                  id="assignedRegion"
                  value={formData.assignedRegion}
                  onChange={e => handleChange('assignedRegion', e.target.value)}
                  placeholder="e.g. Khulna, Dhaka, etc."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="experienceLevel">Experience Level</Label>
                <Select
                  value={formData.experienceLevel}
                  onValueChange={value =>
                    handleChange('experienceLevel', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.repType === 'rep3' && (
              <div className="space-y-2 mt-4">
                <Label htmlFor="organization">
                  Organization <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="organization"
                  value={formData.organization}
                  onChange={e => handleChange('organization', e.target.value)}
                  placeholder="Partner agency or organization name"
                  className={errors.organization ? 'border-red-500' : ''}
                />
                {errors.organization && (
                  <p className="text-sm text-red-500">{errors.organization}</p>
                )}
              </div>
            )}
          </div>

          {/* Commission */}
          <div className="space-y-2">
            <Label htmlFor="commissionPercentage">Commission Rate (%)</Label>
            <div className="relative">
              <Input
                id="commissionPercentage"
                type="number"
                min="0"
                max="100"
                value={formData.commissionPercentage}
                onChange={e =>
                  handleChange(
                    'commissionPercentage',
                    parseInt(e.target.value) || 0,
                  )
                }
                placeholder="10"
                className={`pr-10 ${errors.commissionPercentage ? 'border-red-500' : ''}`}
              />
              <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            {errors.commissionPercentage && (
              <p className="text-sm text-red-500">
                {errors.commissionPercentage}
              </p>
            )}
            <p className="text-xs text-gray-500">
              Commission percentage earned from student enrollments and services
            </p>
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">
              Password <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={e => handleChange('password', e.target.value)}
                  placeholder="Enter password"
                  className={`pr-10 ${errors.password ? 'border-red-500' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={generatePassword}
                title="Generate password"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password}</p>
            )}
            <p className="text-xs text-gray-500">
              Password must be at least 6 characters long
            </p>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Representative'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
